import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Phone, Clock, X } from "lucide-react";
import { OTPInput } from "../../../../components/Inputs";
import {
  sendPhoneVerification,
  verifyPhoneOtpApi,
} from "../../../../api/authAPI";
import { toast } from "react-toastify";
import Loader from "./Loader";
import flag from "../../../../assets/in.png";
import { VerifiedBadge } from "./VerifiedBadge";
import {
  formatOtpCountdown,
  getOtpCooldownSeconds,
  getOtpRetryAfterSeconds,
} from "../../../../utils/otpCooldown";

// Common country codes

export default function PhoneVerification({ isEditing }: any) {
  const { watch, register, setValue, getValues } = useFormContext();

  const phone = watch("phone");
  const originalPhone = getValues("phoneOriginal"); // store initial phone
  const dialCode = watch("dialCode") || "+91";
  const phoneVerified = watch("phoneVerified");

  const [otp, setOtp] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  useEffect(() => {
    if (!countdown) return;
    const t = setInterval(() => setCountdown((v) => v - 1), 1000);
    return () => clearInterval(t);
  }, [countdown]);
  useEffect(() => {
    const currentPhone = getValues("phone");
    const originalPhone = getValues("phoneOriginal");

    if (!originalPhone) {
      setValue("phoneOriginal", currentPhone);
    }
  }, []);

  const sendOtp = async () => {
    if (!phone || phone.length !== 10) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }

    try {
      setOtpLoading(true);
      const res = await sendPhoneVerification({
        newPhone: phone,
        dialCode,
      });

      if (res.data.status) {
        toast.success(res.data.message);
        setCountdown(getOtpCooldownSeconds(res.data));
        setShowModal(true);
      } else {
        const retryAfterSeconds = getOtpRetryAfterSeconds(res.data);
        if (retryAfterSeconds) {
          setCountdown(retryAfterSeconds);
        }
        toast.error(res.data.message);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const retryAfterSeconds = getOtpRetryAfterSeconds(data);
      if (retryAfterSeconds) {
        setCountdown(retryAfterSeconds);
      }
      toast.error(err?.message || data?.message || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleUpdate = () => {
    if (!phone || phone.length !== 10) {
      toast.error("Phone number must be 10 digits");
      return;
    }

    if (!/^[0-9]{10}$/.test(phone)) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }

    setIsUpdatingPhone(true);
    sendOtp();
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error("Enter 6-digit OTP");
      return;
    }

    try {
      setOtpLoading(true);
      const res = await verifyPhoneOtpApi({
        otp,
        newPhone: phone,
        dialCode,
        phoneVerified: true,
      });

      if (res.data.status) {
        toast.success("Mobile number updated successfully");
        setValue("phoneOriginal", res.data.phone);
        setValue("phoneVerified", true);
        setValue("phone", res.data.phone);

        setShowModal(false);
        setOtp("");
        setIsUpdatingPhone(false);
      } else {
        toast.error(res.data.message || "Invalid OTP");
      }
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message || e?.message || "Verification failed",
      );
    } finally {
      setOtpLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setOtp("");
    setIsUpdatingPhone(false);
    setCountdown(0);
  };

  return (
    <>
      {otpLoading && <Loader text="Verifying phone number..." />}

      <div className="space-y-3">
        <label className=" text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
          <Phone size={16} />
          Phone Number *
        </label>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative flex-1 flex gap-2">
            <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg cursor-not-allowed whitespace-nowrap">
              <img
                src={flag}
                srcSet="https://flagcdn.com/w40/in.png 2x"
                width="20"
                height="15"
                alt="India flag"
                className=" object-cover"
              />
              <span className="text-gray-700 text-sm font-medium">IN</span>
              <span className="text-gray-500 text-sm">+91</span>
            </div>

            {/* Hidden field to keep form value */}
            <input type="hidden" {...register("dialCode")} value="+91" />

            {/* Phone Number Input */}
            <div className=" relative flex-1">
              <input
                type="tel"
                value={phone || ""}
                disabled={!isEditing}
                placeholder="Enter phone number"
                className="input "
                inputMode="numeric"
                maxLength={10}
                {...register("phone", {
                  onChange: (e) => {
                    let value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) {
                      setValue("phone", value, { shouldValidate: true });
                    }
                  },
                })}
              />
              {isEditing && phone !== originalPhone && (
                <button
                  onClick={handleUpdate}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                >
                  Update
                </button>
              )}
            </div>
          </div>

          {phoneVerified ? (
            <VerifiedBadge verified={phoneVerified} />
          ) : (
            <VerifiedBadge verified={phoneVerified} />
          )}
        </div>
      </div>

      {/* OTP Verification Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {isUpdatingPhone ? "Update Phone" : "Verify Phone"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="text-blue-600" size={32} />
                </div>
                <p className="text-sm text-gray-600">
                  We've sent a 6-digit verification code to
                </p>
                <p className="font-medium text-gray-900 mt-1">
                  {dialCode} {phone}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter OTP
                  </label>
                  <OTPInput
                    value={otp}
                    onChange={(val: string) => setOtp(val)}
                    length={6}
                    autoFocus={true}
                    className="text-black"
                  />
                </div>

                <button
                  onClick={verifyOtp}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium"
                >
                  Verify OTP
                </button>

                <div className="text-center">
                  <button
                    onClick={sendOtp}
                    disabled={countdown > 0}
                    className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {countdown > 0 ? (
                      <span className="flex items-center justify-center gap-1">
                        <Clock size={14} />
                        Resend in {formatOtpCountdown(countdown)}
                      </span>
                    ) : (
                      "Resend Code"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
